import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, Mail, ShieldCheck, CheckCircle2, AlertCircle, ArrowLeft, Loader2, Sparkles, Layers, Users } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSession } from '../slice/authSlice';
import type { AppDispatch } from '../store/store';
import { API_V1_BASE_URL } from '../config/api';

interface LoginResponse {
    success: boolean;
    message?: string;
    requiresPasswordReset?: boolean;
    resetToken?: string;
    requiresTncAcceptance?: boolean;
    tncAcceptanceToken?: string;
    tnc?: { id: string; version: string; content: string; };
    data?: {
        accessToken: string;
        refreshToken: string;
        user: {
            id: string;
            email: string;
            firstName: string;
            lastName: string;
            role: string;
            tenantId: string;
            permissions?: string[];
        };
    };
}

interface ForgotPasswordResponse { success: boolean; message?: string; }
interface ResetPasswordResponse { success: boolean; message?: string; }

const PASSWORD_RULES = {
    minLength: (p: string) => p.length >= 12,
    uppercase: (p: string) => /[A-Z]/.test(p),
    lowercase: (p: string) => /[a-z]/.test(p),
    number: (p: string) => /\d/.test(p),
    special: (p: string) => /[@$!%*?&]/.test(p),
};

const getRedirectPath = (role: string) => {
    const r = role.toLowerCase().replace(/[\s-]+/g, '_');
    if (r === 'super_admin') return '/super-admin/super-admin-dashboard';
    if (r === 'guest') return '/guest/dashboard';
    if (r === 'customer_manager') return '/customer-manager/dashboard';
    return '/tenant/dashboard';
};

type ViewMode = 'login' | 'forgot' | 'reset';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const locationState = location.state as { email?: string; password?: string; resetSuccess?: boolean } | null;

    const [email, setEmail] = useState(locationState?.email || '');
    const [password, setPassword] = useState(locationState?.password || '');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const resetSuccess = Boolean(locationState?.resetSuccess);

    const [view, setView] = useState<ViewMode>('login');
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetError, setResetError] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    const passwordValidation = {
        minLength: PASSWORD_RULES.minLength(newPassword),
        uppercase: PASSWORD_RULES.uppercase(newPassword),
        lowercase: PASSWORD_RULES.lowercase(newPassword),
        number: PASSWORD_RULES.number(newPassword),
        special: PASSWORD_RULES.special(newPassword),
    };
    const isPasswordValid = Object.values(passwordValidation).every(Boolean);

    useEffect(() => {
        const tokenFromUrl = new URLSearchParams(location.search).get('accessToken');
        if (tokenFromUrl) {
            setResetToken(tokenFromUrl);
            setView('reset');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();

        if (!trimmedEmail || !trimmedPassword) {
            setError('Please enter both email and password');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_V1_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword }),
            });
            const result = await response.json().catch(() => null) as LoginResponse | null;

            if (!response.ok) throw new Error(result?.message || 'Invalid email or password.');

            if (result?.requiresPasswordReset) {
                if (!result.resetToken) throw new Error('Unable to start password reset. Please contact support.');
                setResetToken(result.resetToken);
                setView('reset');
                return;
            }

            if (result?.requiresTncAcceptance) {
                if (!result.tnc) throw new Error('Unable to load Terms and Conditions. Please contact support.');
                if (!result.tncAcceptanceToken) throw new Error('Unable to start Terms and Conditions acceptance. Please login again.');
                navigate('/tenant-tnc', {
                    state: { email: trimmedEmail, password: trimmedPassword, tnc: result.tnc, tncAcceptanceToken: result.tncAcceptanceToken },
                });
                return;
            }

            if (!result?.data) throw new Error(result?.message || 'Invalid email or password.');

            const { accessToken, refreshToken, user } = result.data;
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("user", JSON.stringify({ ...user, loginTime: new Date().toISOString() }));
            dispatch(setSession({ user: { ...user, permissions: user.permissions || [] }, accessToken, refreshToken }));
            navigate(getRedirectPath(user.role));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const openForgotPassword = () => {
        setForgotEmail(email);
        setForgotError('');
        setForgotSent(false);
        setView('forgot');
    };

    const backToLogin = () => {
        setView('login');
        setForgotError('');
        setForgotSent(false);
        setResetError('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotError('');
        const trimmedForgotEmail = forgotEmail.trim();
        if (!trimmedForgotEmail) { setForgotError('Please enter your email'); return; }

        setForgotLoading(true);
        try {
            const response = await fetch(`${API_V1_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: trimmedForgotEmail }),
            });
            const result = await response.json().catch(() => null) as ForgotPasswordResponse | null;
            if (!response.ok || !result?.success) throw new Error(result?.message || 'Something went wrong. Please try again.');
            setForgotSent(true);
        } catch (err) {
            setForgotError(err instanceof Error ? err.message : 'Unable to send reset email.');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetError('');
        if (!newPassword || !confirmPassword) { setResetError('Please fill in all fields'); return; }
        if (!isPasswordValid) { setResetError('Password must contain minimum 12 characters with uppercase, lowercase, number and special character.'); return; }
        if (newPassword !== confirmPassword) { setResetError('New password and confirmation do not match'); return; }
        if (!resetToken) { setResetError('Invalid reset link'); return; }

        setResetLoading(true);
        try {
            const response = await fetch(`${API_V1_BASE_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: resetToken, newPassword }),
            });
            const result = await response.json().catch(() => null) as ResetPasswordResponse | null;
            if (!response.ok || !result?.success) throw new Error(result?.message || 'Unable to reset password. Please try again.');
            setNewPassword('');
            setConfirmPassword('');
            setResetToken('');
            setView('login');
            navigate('/login', { state: { resetSuccess: true }, replace: true });
        } catch (err) {
            setResetError(err instanceof Error ? err.message : 'Unable to reset password. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-slate-50">
            {/* ================= Brand Panel ================= */}
            <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
                {/* Decorative grid */}
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
                        backgroundSize: '44px 44px',
                    }}
                />
                {/* Glow */}
                <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />

                <div className="relative z-10 flex flex-col justify-between w-full px-12 py-14 text-white">
                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                            <Sparkles size={18} className="text-white" />
                        </div>
                        <span className="text-lg font-semibold tracking-tight">SIMB</span>
                    </div>

                    {/* Hero copy */}
                    <div className="max-w-md">
                        <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
                            Welcome to <span className="bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">SIMB</span>
                        </h1>
                        <p className="text-blue-100/80 text-lg mt-4">
                            Multi-Tenant Platform Management — centralized, secure, and built for scale.
                        </p>

                        <ul className="mt-10 space-y-4">
                            {[
                                { icon: Layers, label: 'Centralized Platform Control' },
                                { icon: Users, label: 'Multi-Tenant Management' },
                                { icon: ShieldCheck, label: 'White-Label Solutions' },
                            ].map(({ icon: Icon, label }) => (
                                <li key={label} className="flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
                                        <Icon size={15} className="text-blue-200" />
                                    </span>
                                    <span className="text-blue-50/90 text-sm">{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Footer */}
                    <p className="text-xs text-blue-200/60">
                        © {new Date().getFullYear()} SIMB. All rights reserved.
                    </p>
                </div>
            </div>

            {/* ================= Form Panel ================= */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    {/* Mobile brand */}
                    <div className="lg:hidden text-center mb-8">
                        <div className="inline-flex items-center gap-2 mb-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
                                <Sparkles size={16} className="text-white" />
                            </div>
                            <span className="text-xl font-bold text-slate-900">SIMB</span>
                        </div>
                        <p className="text-sm text-slate-500">Multi-Tenant Platform</p>
                    </div>

                    {/* ========== LOGIN VIEW ========== */}
                    {view === 'login' && (
                        <>
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-8 sm:p-10">
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl font-bold text-slate-900">Welcome Back</h2>
                                    <p className="text-sm text-slate-500 mt-2">Sign in to your account to continue</p>
                                </div>

                                <form onSubmit={handleLogin} className="space-y-5">
                                    {resetSuccess && (
                                        <Alert tone="success">
                                            Your password has been reset. Please sign in with your new password.
                                        </Alert>
                                    )}
                                    {error && <Alert tone="error">{error}</Alert>}

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                                        <div className="relative">
                                            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition"
                                                placeholder="you@company.com"
                                                required
                                                autoComplete="username"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-slate-700">Password</label>
                                            <button
                                                type="button"
                                                onClick={openForgotPassword}
                                                className="text-xs font-medium text-blue-600 hover:text-blue-700 transition"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-12 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition"
                                                placeholder="••••••••"
                                                required
                                                autoComplete="current-password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Remember me */}
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/40"
                                        />
                                        <span className="text-sm text-slate-600">Remember me</span>
                                    </label>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition font-medium text-sm shadow-lg shadow-blue-600/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Signing in...
                                            </>
                                        ) : (
                                            'Sign In'
                                        )}
                                    </button>
                                </form>

                                <div className="mt-6 text-center text-sm text-slate-500">
                                    Don't have an account?{' '}
                                    <a href="#" className="text-blue-600 hover:text-blue-700 font-medium transition">
                                        Contact Super Admin
                                    </a>
                                </div>
                            </div>

                            <p className="mt-6 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                                <ShieldCheck size={13} />
                                Secured by enterprise-grade encryption
                            </p>
                        </>
                    )}

                    {/* ========== FORGOT VIEW ========== */}
                    {view === 'forgot' && (
                        <>
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-8 sm:p-10">
                                {forgotSent ? (
                                    <div className="text-center">
                                        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                                            <CheckCircle2 size={26} className="text-emerald-600" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Check your email</h2>
                                        <p className="text-sm text-slate-500 mt-2">
                                            If an account exists for{' '}
                                            <span className="font-medium text-slate-800">{forgotEmail}</span>, we've sent a link to reset your password.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={backToLogin}
                                            className="mt-6 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition"
                                        >
                                            <ArrowLeft size={15} /> Back to sign in
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={backToLogin}
                                            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6 transition"
                                        >
                                            <ArrowLeft size={14} /> Back to sign in
                                        </button>

                                        <div className="text-center mb-8">
                                            <h2 className="text-2xl font-bold text-slate-900">Reset your password</h2>
                                            <p className="text-sm text-slate-500 mt-2">
                                                Enter your email and we'll send you a reset link
                                            </p>
                                        </div>

                                        <form onSubmit={handleForgotSubmit} className="space-y-5">
                                            {forgotError && <Alert tone="error">{forgotError}</Alert>}

                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                                                <div className="relative">
                                                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                                    <input
                                                        type="email"
                                                        value={forgotEmail}
                                                        onChange={(e) => setForgotEmail(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition"
                                                        placeholder="you@company.com"
                                                        required
                                                        autoComplete="username"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={forgotLoading}
                                                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition font-medium text-sm shadow-lg shadow-blue-600/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {forgotLoading ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    'Send Reset Link'
                                                )}
                                            </button>
                                        </form>
                                    </>
                                )}
                            </div>

                            <p className="mt-6 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                                <ShieldCheck size={13} />
                                Secured by enterprise-grade encryption
                            </p>
                        </>
                    )}

                    {/* ========== RESET VIEW ========== */}
                    {view === 'reset' && (
                        <>
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-8 sm:p-10">
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl font-bold text-slate-900">Set a New Password</h2>
                                    <p className="text-sm text-slate-500 mt-2">
                                        Please choose a new password to continue.
                                    </p>
                                </div>

                                <form onSubmit={handleResetSubmit} className="space-y-5">
                                    {resetError && <Alert tone="error">{resetError}</Alert>}

                                    {/* New password */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                                        <div className="relative">
                                            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full pl-10 pr-12 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition"
                                                placeholder="••••••••"
                                                required
                                                autoComplete="new-password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                                                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>

                                        {/* Rule checklist */}
                                        <div className="grid grid-cols-2 gap-1.5 mt-3">
                                            <Rule ok={passwordValidation.minLength} label="12+ characters" />
                                            <Rule ok={passwordValidation.uppercase} label="Uppercase" />
                                            <Rule ok={passwordValidation.lowercase} label="Lowercase" />
                                            <Rule ok={passwordValidation.number} label="Number" />
                                            <Rule ok={passwordValidation.special} label="Special (@$!%*?&)" />
                                        </div>
                                    </div>

                                    {/* Confirm password */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
                                        <div className="relative">
                                            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className={`w-full pl-10 pr-12 py-2.5 border rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 outline-none transition ${confirmPassword && newPassword !== confirmPassword
                                                    ? 'border-red-400 focus:ring-red-500/20 focus:border-red-500'
                                                    : 'border-slate-300 focus:ring-blue-500/30 focus:border-blue-500'
                                                    }`}
                                                placeholder="••••••••"
                                                required
                                                autoComplete="new-password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {confirmPassword && (
                                            <div className="mt-2">
                                                {newPassword !== confirmPassword ? (
                                                    <p className="text-xs text-red-500 flex items-center gap-1.5">
                                                        <AlertCircle size={13} /> Passwords do not match
                                                    </p>
                                                ) : isPasswordValid ? (
                                                    <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                                                        <CheckCircle2 size={13} /> Passwords match and meet requirements
                                                    </p>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={resetLoading || !isPasswordValid || newPassword !== confirmPassword}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition font-medium text-sm shadow-lg shadow-blue-600/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {resetLoading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Resetting...
                                            </>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </button>
                                </form>
                            </div>

                            <p className="mt-6 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                                <ShieldCheck size={13} />
                                Secured by enterprise-grade encryption
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ============================================================
   Small presentational helpers — keeps the main JSX readable
   ============================================================ */

const Alert: React.FC<{ tone: 'error' | 'success'; children: React.ReactNode }> = ({ tone, children }) => {
    const styles =
        tone === 'error'
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-emerald-50 border-emerald-200 text-emerald-700';
    const Icon = tone === 'error' ? AlertCircle : CheckCircle2;
    return (
        <div className={`flex items-start gap-2.5 p-3 rounded-xl border text-sm ${styles}`}>
            <Icon size={16} className="mt-0.5 shrink-0" />
            <span>{children}</span>
        </div>
    );
};

const Rule: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
    <div
        className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-slate-400'}`}
    >
        {ok ? <CheckCircle2 size={13} /> : <span className="w-[13px] h-[13px] rounded-full border border-current inline-block" />}
        {label}
    </div>
);

export default Login;